import React from 'react'
import styled from '@emotion/styled'
import {equals, intersection, last} from 'ramda'

import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Offer} from '@vio/core/src/offer/types/offer'

import {useGetRoomOfferLabels} from '../useGetRoomOfferLabels'

const RoomLabelsItem = styled.span`
  margin-right: ${cssTheme.layout.spacing.s200};
`
const Dot = styled.span`
  display: inline-block;
  margin-left: ${cssTheme.layout.spacing.s200};
`
const SingleLineWrapper = styled.span`
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
`
interface Props {
  offers: Offer[]
  shouldShowFreeCancellationDate?: boolean
  // AP sticky bottom bar rendering: truncates the labels to a single line and
  // gives the room-switch note the primary content color.
  isCompact?: boolean
  // Split-booking offers require switching rooms mid-stay: surface that ahead
  // of the (green) deal labels.
  showRoomSwitchRequired?: boolean
}
export const PrimaryRoomOfferLabels = ({
  offers,
  shouldShowFreeCancellationDate = false,
  isCompact = false,
  showRoomSwitchRequired = false
}: Props) => {
  // for SB we'll have two offers, for regular offer - only one
  const offer1Labels = useGetRoomOfferLabels({
    offer: offers?.[0],
    shouldShowFreeCancellationDate
  })
  const offer2Labels = useGetRoomOfferLabels({
    offer: offers?.[1],
    shouldShowFreeCancellationDate
  })

  const labels =
    offer2Labels.length > 0
      ? intersection(offer1Labels, offer2Labels)
      : offer1Labels

  const Wrapper = isCompact ? SingleLineWrapper : React.Fragment
  // Two split-booking rooms rarely share every perk, so their intersection can
  // be empty while the room-switch note still has to show.
  if (labels.length === 0 && !showRoomSwitchRequired) return null

  return (
    <Text variant="bodyXS" colorPath="content.success.secondary">
      <Wrapper>
        {showRoomSwitchRequired && (
          <RoomLabelsItem>
            <Text
              variant="bodyXS"
              colorPath={
                isCompact
                  ? 'content.neutral.primary'
                  : 'content.neutral.secondary'
              }
              tag="span"
            >
              <FormattedMessageWrapper
                id="roomSwitchRequired"
                defaultMessage="Room switch required"
              />
              {labels.length > 0 && <Dot as="span">·</Dot>}
            </Text>
          </RoomLabelsItem>
        )}
        {labels.map(label => (
          <RoomLabelsItem key={label}>
            {label}
            {!equals(last(labels), label) && <Dot as="span">·</Dot>}
          </RoomLabelsItem>
        ))}
      </Wrapper>
    </Text>
  )
}
